import React from 'react';
import Tokens from '@nokia-csf-uxr/freeform-design-tokens';
import _merge from 'lodash/merge';
import CloseIcon from '@nokia-csf-uxr/ccfk-assets/CloseIcon';
import { getContext } from 'common';

import Typography, { TypographyProps } from 'components/Typography';
import IconButton, { IconButtonProps } from 'components/IconButton';

import Styled from './styles';

const BUTTON_CONFIG = RTL => ({
  style: {
    alignSelf: 'flex-start',
  },
  rippleStyle: {
    marginLeft: RTL ? Tokens.SPACING.SPACING_0 : Tokens.SPACING.SPACING_16,
    marginRight: RTL ? Tokens.SPACING.SPACING_16 : Tokens.SPACING.SPACING_0
  }
});

const TYPOGRAPHY_CONFIG = {
  variant: 'TITLE_16',
  style: {
    flex: 1
  }
};

export interface DialogTitleProps extends Omit<React.ComponentPropsWithRef<'div'>, 'title'> {
  /** The content of the title. */
  title: React.ReactNode;
  /** The title typography props. */
  typographyProps?: Partial<TypographyProps>;
  /** Close button configuration, set any property of this prop to show the close button. */
  buttonProps?: Partial<IconButtonProps>;
  /** The content of the close button (`buttonProps` should be set) */
  buttonContent?: React.ReactNode;
  /** To override the theme please refer to the "Readme" tab to learn more. */
  theme?: any;
}

/** All other props will be provided to the wrapper element. */
const DialogTitle = React.forwardRef<HTMLElement, DialogTitleProps>((props: DialogTitleProps, ref) => getContext(({ RTL, theme }) => {
  const {
    title,
    buttonProps,
    theme: themeProp,
    buttonContent,
    typographyProps: givenTypographyProps,
    ...otherProps
  } = props;

  const resolvedTheme = _merge({}, theme ? theme.DIALOG : {}, themeProp);

  const typographyProps = _merge(
    {}, {
      style: {
        color: resolvedTheme.title,
      },
    },
    TYPOGRAPHY_CONFIG,
    givenTypographyProps
  );

  const resolvedButtonConfig = {
    ...buttonProps,
    ...BUTTON_CONFIG(RTL)
  };

  return (
    <Styled.Title
      RTL={RTL}
      {...otherProps}
      ref={ref}
    >
      <Typography maxLines={2} {...typographyProps} >{title}</Typography>
      {!!buttonProps && <IconButton {...resolvedButtonConfig}>{buttonContent}</IconButton>}
    </Styled.Title>
  );
}));

DialogTitle.displayName = 'DialogTitle';

DialogTitle.defaultProps = {
  buttonProps: undefined,
  typographyProps: {},
  buttonContent: <CloseIcon />,
  theme: {}
};

export default DialogTitle;
